'use client'

import { useRef, useState } from 'react'

import { Button } from '@/app/components/Button'
import { Icon } from '@/app/components/Icon'
import { Navigation } from '@/app/components/Navigation'
import { useUser } from '@/app/contexts/UserContext'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { hyphenToCamelCase } from '@/app/utils/text'
import { NavQueryResult } from '@/sanity.types'

// Maps FontAwesome icon names sent by the user menu API to MUI outlined
// icons, mirroring UserMenu in paved-frontend. The icons come from the
// generated MUI registry (see scripts/generate-icon-registry.ts)
const menuIconMap: Record<string, string> = {
  faStar: 'StarBorder',
  faEnvelopeOpenDollar: 'RequestQuote',
  faAd: 'Campaign',
  faHeart: 'FavoriteBorder',
  faFolder: 'Folder',
  faFileInvoiceDollar: 'ReceiptLong',
  faAnalytics: 'QueryStats',
  faBrowser: 'WebAsset',
  faMoneyBillAlt: 'Paid',
  faUsers: 'Groups',
  faCog: 'Settings',
  faSmileBeam: 'SentimentSatisfiedAlt',
  faSignOutAlt: 'Logout',
}

interface HeaderActionNavProps {
  navCta: NavQueryResult[number] | null
  themeMode?: 'light' | 'dark'
  variantMapping?: Record<string, string>
  classNames?: {
    root?: string
    list?: string
    user?: string
    userMenu?: string
    item?: string
    link?: string
  }
}

export const HeaderActionNav = ({
  navCta,
  themeMode,
  variantMapping,
  classNames,
}: HeaderActionNavProps) => {
  const { user, isLoading, logout } = useUser()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useClickOutside(menuRef, () => setIsUserMenuOpen(false), isUserMenuOpen)

  // Prevent flash by rendering nothing during loading
  // Use suppressHydrationWarning on parent to avoid mismatch
  if (isLoading) {
    // Render a placeholder with same height as actual content to prevent CLS
    // Actual content renders at 40px (button height)
    return <div className="flex items-center h-[40px]" suppressHydrationWarning />
  }

  // If user is signed in, display custom menu
  if (user && user.menu) {
    // Apply variant mapping (navbarLink → navbarLinkDark in light mode, etc.)
    const userButtonVariant = (variantMapping?.['navbarLink'] || 'navbarLink') as
      | 'navbarLink'
      | 'navbarLinkDark'

    return (
      <div
        ref={menuRef}
        className={cn('relative flex items-center', classNames?.root)}
        suppressHydrationWarning
      >
        <Button
          button={{
            _type: 'button',
            variant: userButtonVariant,
            size: null,
            link: undefined,
            icon: undefined,
            iconPlacement: 'none',
            dataId: null,
          }}
          className={cn(
            'flex items-center gap-s',
            classNames?.user,
            isUserMenuOpen && (themeMode === 'dark' ? 'text-paved-blue-200' : 'text-green-700'),
          )}
          tracking={{ location: TRACKING_LOCATIONS.HEADER }}
          type="button"
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
        >
          {user.first_name} {user.last_name}, {user.company_name}
          <Icon
            name="ExpandMore"
            provider="mui"
            style="filled"
            size={20}
            className={cn('transition-transform duration-200', isUserMenuOpen && 'rotate-180')}
          />
        </Button>
        <nav
          className={cn(
            'absolute top-full right-0 pt-lg w-max overflow-y-auto transition-opacity duration-300 min-w-[280px] max-h-[calc(100dvh-180px)]',
            isUserMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible',
            classNames?.userMenu,
          )}
        >
          {user.menu.map((menuItem, index) => {
            const muiIconName = menuItem.icon
              ? menuIconMap[hyphenToCamelCase(menuItem.icon)]
              : undefined

            return (
              <a
                key={index}
                href={`https://www.paved.com${menuItem.link}`}
                className={cn(
                  'flex flex-row items-center gap-lg p-lg size-body-sm text-white-950 bg-neutral-50 hover:bg-neon-100 active:bg-neon-200 transition-colors duration-200 cursor-pointer',
                  index === 0 && 'rounded-t-xs',
                  index === user.menu.length - 1 && 'rounded-b-xs',
                )}
                onClick={(e) => {
                  const isSignOut = menuItem.link?.includes('sign_out') ?? false

                  if (isSignOut) {
                    logout()
                    setIsUserMenuOpen(false)
                  }

                  if (menuItem.reload) {
                    // Allow default behavior (full page reload)
                    return
                  }

                  e.preventDefault()
                  window.location.href = `https://www.paved.com${menuItem.link}`
                }}
              >
                {muiIconName && (
                  <Icon
                    name={muiIconName}
                    provider="mui"
                    style="outlined"
                    size={20}
                    className="text-white-600"
                  />
                )}
                {menuItem.name}
              </a>
            )
          })}
        </nav>
      </div>
    )
  }

  // Default: display navCta Navigation component for non-signed-in users
  if (navCta) {
    return (
      <div className={classNames?.root} suppressHydrationWarning>
        <Navigation
          nav={navCta}
          tracking={{ location: TRACKING_LOCATIONS.HEADER }}
          themeMode={themeMode}
          variantMapping={variantMapping}
          classNames={{
            root: cn('flex items-center'),
            list: cn('flex flex-row items-center gap-md', classNames?.list),
            item: classNames?.item,
            link: cn('size-header-h6', classNames?.link),
            button: '!size-button-l',
          }}
        />
      </div>
    )
  }

  return null
}
